import {
    DownloadOutlined as BatchDownloadOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    DeleteOutlined,
    DownloadOutlined,
    ExclamationCircleOutlined,
    EyeOutlined,
    FileTextOutlined,
    ReloadOutlined,
    SyncOutlined
} from '@ant-design/icons';
import {
    Alert,
    Button,
    Card,
    Col,
    DatePicker,
    Descriptions,
    Empty,
    List,
    message,
    Modal,
    Progress,
    Row,
    Select,
    Space,
    Statistic,
    Table,
    Tag,
    Tooltip
} from 'antd';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { batchDownloadTaskResults, cleanupTempFile, deleteTask, downloadTaskResult, getTaskFiles, getUserTasks } from '../services/api';
import './TaskHistory.less';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { confirm } = Modal;

const TaskHistory = () => {
  console.log('TaskHistory component rendering');
  const { user, isAuthenticated } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskFiles, setTaskFiles] = useState([]);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [filesModalVisible, setFilesModalVisible] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState([]);
  const [statistics, setStatistics] = useState({
    total: 0,
    completed: 0,
    failed: 0,
    processing: 0
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [batchDownloadLoading, setBatchDownloadLoading] = useState(false);

  // 加载任务列表
  const loadTasks = useCallback(async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    try {
      const params = {
        limit: 100,
        offset: 0
      };

      // 添加状态过滤
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      // 添加日期过滤
      if (dateRange.length === 2) {
        params.start_date = dateRange[0].format('YYYY-MM-DD');
        params.end_date = dateRange[1].format('YYYY-MM-DD');
      }

      console.log('Loading tasks with params:', params);
      const response = await getUserTasks(params);
      console.log('API Response:', response);
      const taskList = Array.isArray(response) ? response : [];
      console.log('Task List:', taskList);
      setTasks(taskList);

      // 计算统计信息
      const stats = {
        total: taskList.length,
        completed: taskList.filter(t => t.status === 'completed').length,
        failed: taskList.filter(t => t.status === 'failed').length,
        processing: taskList.filter(t => t.status === 'processing').length
      };
      console.log('Statistics:', stats);
      setStatistics(stats);

    } catch (error) {
      console.error('加载任务列表失败:', error);
      message.error(`加载任务列表失败: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, statusFilter, dateRange]);

  // 组件挂载时加载数据
  useEffect(() => {
    console.log('TaskHistory component mounted, isAuthenticated:', isAuthenticated);
    if (isAuthenticated) {
      loadTasks();
    }
  }, [loadTasks, isAuthenticated]);

  // 获取任务状态标签
  const getStatusTag = (status) => {
    const statusConfig = {
      pending: { color: 'default', icon: <ClockCircleOutlined />, text: '等待中' },
      processing: { color: 'blue', icon: <SyncOutlined spin />, text: '处理中' },
      completed: { color: 'green', icon: <CheckCircleOutlined />, text: '已完成' },
      failed: { color: 'red', icon: <CloseCircleOutlined />, text: '失败' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  // 获取进度条颜色
  const getProgressColor = (status, progress) => {
    if (status === 'completed') return '#52c41a';
    if (status === 'failed') return '#ff4d4f';
    if (status === 'processing') return '#1890ff';
    return '#d9d9d9';
  };

  // 查看任务详情
  const handleViewDetails = (task) => {
    setSelectedTask(task);
    setDetailModalVisible(true);
  };

  // 查看失败文件
  const handleViewFiles = async (task) => {
    setSelectedTask(task);
    setLoading(true);
    try {
      const response = await getTaskFiles(task.id);
      setTaskFiles(response.files || []);
      setFilesModalVisible(true);
    } catch (error) {
      console.error('获取文件列表失败:', error);
      message.error(`获取文件列表失败: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // 下载结果
  const handleDownload = async (task) => {
    try {
      message.loading('正在准备下载...', 0);
      await downloadTaskResult(task.id);
      message.destroy();
      message.success('下载成功');
    } catch (error) {
      message.destroy();
      console.error('下载失败:', error);
      message.error(`下载失败: ${error.message}`);
    }
  };

  // 删除任务
  const handleDelete = (task) => {
    confirm({
      title: '确认删除',
      icon: <ExclamationCircleOutlined />,
      content: `确定要删除任务"${task.task_name}"吗？此操作不可恢复。`,
      okText: '确认删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await deleteTask(task.id);
          message.success('任务删除成功');
          loadTasks(); // 重新加载列表
        } catch (error) {
          console.error('删除任务失败:', error);
          message.error(`删除任务失败: ${error.message}`);
        }
      }
    });
  };

  // 批量下载
  const handleBatchDownload = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要下载的任务');
      return;
    }

    // 过滤出已完成且有处理文件的任务
    const validTasks = tasks.filter(task => 
      selectedRowKeys.includes(task.id) && 
      task.status === 'completed' && 
      task.processed_files > 0
    );

    if (validTasks.length === 0) {
      message.warning('所选任务中没有可下载的已完成任务');
      return;
    }

    setBatchDownloadLoading(true);
    try {
      const taskIds = validTasks.map(task => task.id);
      
      // 调用批量下载API
      const result = await batchDownloadTaskResults(taskIds);
      
      message.success(`成功下载 ${validTasks.length} 个任务的聚合数据`);
      
      // 清空选择
      setSelectedRowKeys([]);

      // 清理临时文件
      if (result.tempFilePath) {
        // 延迟清理临时文件
        setTimeout(async () => {
          try {
            await cleanupTempFile(result.tempFilePath);
            console.log('临时文件清理成功');
          } catch (error) {
            console.warn('清理临时文件失败:', error);
          }
        }, 5000); // 5秒后清理
      }

    } catch (error) {
      console.error('批量下载失败:', error);
      message.error(`批量下载失败: ${error.message}`);
    } finally {
      setBatchDownloadLoading(false);
    }
  };

  // 判断是否为最近1天的任务（保留用于显示标签，但不用于限制下载）
  const isRecentTask = (createdAt) => {
    const taskDate = dayjs(createdAt);
    const oneDayAgo = dayjs().subtract(1, 'day');
    return taskDate.isAfter(oneDayAgo);
  };

  // 行选择配置
  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    getCheckboxProps: (record) => ({
      disabled: record.status !== 'completed' || record.processed_files === 0,
    }),
  };

  // 表格列定义
  const columns = [
    {
      title: '任务名称',
      dataIndex: 'task_name',
      key: 'task_name',
      ellipsis: true,
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            ID: {record.id.slice(-8)}
          </div>
        </div>
      )
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => getStatusTag(status)
    },
    {
      title: '进度',
      dataIndex: 'progress',
      key: 'progress',
      width: 120,
      render: (progress, record) => (
        <Progress
          percent={Math.round(progress)}
          size="small"
          strokeColor={getProgressColor(record.status, progress)}
          showInfo={progress > 0}
        />
      )
    },
    {
      title: '文件统计',
      key: 'files',
      width: 120,
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          <div>总计: {record.total_files}</div>
          <div style={{ color: '#52c41a' }}>成功: {record.processed_files}</div>
          {record.failed_files > 0 && (
            <div style={{ color: '#ff4d4f' }}>失败: {record.failed_files}</div>
          )}
        </div>
      )
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (text) => (
        <div>
          <div>{dayjs(text).format('MM-DD HH:mm')}</div>
          {isRecentTask(text) && (
            <Tag color="orange" size="small">最近1天</Tag>
          )}
        </div>
      )
    },
    {
      title: '操作',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="查看详情">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          
          {record.failed_files > 0 && (
            <Tooltip title="查看失败文件">
              <Button
                type="text"
                size="small"
                icon={<FileTextOutlined />}
                onClick={() => handleViewFiles(record)}
              />
            </Tooltip>
          )}
          
          {record.status === 'completed' && record.processed_files > 0 && (
            <Tooltip title="下载结果">
              <Button
                type="text"
                size="small"
                icon={<DownloadOutlined />}
                onClick={() => handleDownload(record)}
              />
            </Tooltip>
          )}
          
          <Tooltip title="删除任务">
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  // 如果用户未登录
  if (!isAuthenticated) {
    console.log('User not authenticated, showing login message');
    return (
      <Card>
        <Alert
          message="需要登录"
          description="请先登录以查看您的历史任务"
          type="warning"
          showIcon
        />
      </Card>
    );
  }

  // 添加一个简单的测试渲染
  return (
    <div className="task-history">
      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={12} md={6}>
          <Card>
            <Statistic
              title="总任务数"
              value={statistics.total}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card>
            <Statistic
              title="已完成"
              value={statistics.completed}
              valueStyle={{ color: '#3f8600' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card>
            <Statistic
              title="处理中"
              value={statistics.processing}
              valueStyle={{ color: '#1890ff' }}
              prefix={<SyncOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card>
            <Statistic
              title="失败"
              value={statistics.failed}
              valueStyle={{ color: '#cf1322' }}
              prefix={<CloseCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* 过滤器 */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ marginRight: 8, whiteSpace: 'nowrap' }}>状态筛选:</span>
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                style={{ width: '100%' }}
              >
                <Option value="all">全部</Option>
                <Option value="pending">等待中</Option>
                <Option value="processing">处理中</Option>
                <Option value="completed">已完成</Option>
                <Option value="failed">失败</Option>
              </Select>
            </div>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ marginRight: 8, whiteSpace: 'nowrap' }}>日期范围:</span>
              <RangePicker
                value={dateRange}
                onChange={setDateRange}
                format="YYYY-MM-DD"
                placeholder={['开始日期', '结束日期']}
                style={{ width: '100%' }}
              />
            </div>
          </Col>
          <Col xs={12} sm={12} md={4}>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadTasks}
              loading={loading}
              block
            >
              刷新
            </Button>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Tooltip title="会将选中的多个 Excel 文件数据进行合并到同一个 Excel 中。">
              <Button
                type="primary"
                icon={<BatchDownloadOutlined />}
                onClick={handleBatchDownload}
                loading={batchDownloadLoading}
                disabled={selectedRowKeys.length === 0}
                block
              >
                合并下载 ({selectedRowKeys.length})
              </Button>
            </Tooltip>
          </Col>
        </Row>
      </Card>
      {/* 任务列表 */}
      <Card>
        <Table
          columns={columns}
          dataSource={tasks}
          rowKey="id"
          loading={loading}
          rowSelection={rowSelection}
          scroll={{ x: 1000 }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 个任务`
          }}
          locale={{
            emptyText: <Empty description="暂无任务数据" />
          }}
        />
      </Card>

      {/* 任务详情模态框 */}
      <Modal
        title="任务详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={600}
      >
        {selectedTask && (
          <Descriptions column={2} bordered>
            <Descriptions.Item label="任务名称" span={2}>
              {selectedTask.task_name}
            </Descriptions.Item>
            <Descriptions.Item label="任务ID" span={2}>
              {selectedTask.id}
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              {getStatusTag(selectedTask.status)}
            </Descriptions.Item>
            <Descriptions.Item label="进度">
              <Progress
                percent={Math.round(selectedTask.progress)}
                size="small"
                strokeColor={getProgressColor(selectedTask.status, selectedTask.progress)}
              />
            </Descriptions.Item>
            <Descriptions.Item label="总文件数">
              {selectedTask.total_files}
            </Descriptions.Item>
            <Descriptions.Item label="已处理">
              {selectedTask.processed_files}
            </Descriptions.Item>
            <Descriptions.Item label="失败文件">
              {selectedTask.failed_files}
            </Descriptions.Item>
            <Descriptions.Item label="文档类型">
              {selectedTask.doc_type}
            </Descriptions.Item>
            <Descriptions.Item label="创建时间" span={2}>
              {dayjs(selectedTask.created_at).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
            {selectedTask.completed_at && (
              <Descriptions.Item label="完成时间" span={2}>
                {dayjs(selectedTask.completed_at).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
            )}
            {selectedTask.error_message && (
              <Descriptions.Item label="错误信息" span={2}>
                <div style={{ color: '#ff4d4f' }}>
                  {selectedTask.error_message}
                </div>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>

      {/* 文件列表模态框 */}
      <Modal
        title={`任务文件列表 - ${selectedTask?.task_name}`}
        open={filesModalVisible}
        onCancel={() => setFilesModalVisible(false)}
        footer={null}
        width={800}
      >
        <List
          dataSource={taskFiles}
          renderItem={(file) => (
            <List.Item>
              <List.Item.Meta
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{file.file_name}</span>
                    {getStatusTag(file.processing_status)}
                  </div>
                }
                description={
                  <div>
                    <div>文件大小: {(file.file_size / 1024).toFixed(1)} KB</div>
                    {file.processing_time && (
                      <div>处理时间: {file.processing_time.toFixed(2)} 秒</div>
                    )}
                    {file.error_message && (
                      <div style={{ color: '#ff4d4f', marginTop: 4 }}>
                        错误: {file.error_message}
                      </div>
                    )}
                  </div>
                }
              />
            </List.Item>
          )}
          locale={{
            emptyText: <Empty description="暂无文件数据" />
          }}
        />
      </Modal>
    </div>
  );
};

export default TaskHistory; 